import type { Language } from '@eformulieren/engine'
import type { Projection } from '@eformulieren/model'
import {
  type AdviceLine,
  type AdviceMarks,
  type AdviceTarget,
  type ShareStart,
  type SidePanel,
  AdvicePanel,
  adviceCountFor,
  adviceNews,
} from '@eformulieren/renderer'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type AdviceRecord, type ReadAdviceFull, adviceViews, panelRequests, showAdvicePanel } from './advice.js'
import { createShareClient } from './shareClient.js'
import type { ReadAdvice, ReadOverview } from './sharing.js'

/**
 * Advies op de formulierpagina (R-184, R-184b, R-184c, ontwerp 7.4a): één lezing van `GET /advies`
 * en `GET /delingen` voor het paneel, de ballonnen en labels bij de vragen, de dialoog en de knop in
 * de kop. Eén bron, zodat ze het nooit oneens zijn over `kanVragen` of over wat er gevraagd is.
 *
 * **Alleen aan de interne kant.** Aan de externe kant vraagt deze hook niets op en geeft hij niets
 * terug: geen paneel, geen ballon, geen lege plek (R-184b).
 *
 * **Nieuw advies komt binnen door opnieuw te lezen**, elke 30 seconden zolang het tabblad zichtbaar
 * is, en meteen na elke handeling. Er is geen kanaal dat de pagina iets toestuurt. Wat er sinds de
 * vorige lezing bij kwam, gaat als `melding` naar de live regio van het formulier, buiten het paneel:
 * op een smal scherm is het paneel in de weergave Formulier verborgen.
 */

export interface AdviceOptions {
  readonly taal: Language
  readonly instantieId: string
  /** Niets zolang het formulier er nog niet is: dan doet de hook niets. */
  readonly projectie: Projection | undefined
  /** De vaste sleutel van de kijker. */
  readonly ik: string
  readonly headers: Readonly<Record<string, string>>
  readonly onOpenWith: (start: ShareStart | undefined) => void
  readonly onSessionExpired: () => void
}

export interface PageAdvice {
  /** Voor de knop en de dialoog: wat er bij advies te kiezen is, en de regels voor de lijst. */
  readonly forDialog: ReadAdvice | 'laden'
  readonly marks: AdviceMarks | undefined
  readonly sidePanel: SidePanel | undefined
  /** Opnieuw lezen, na een handeling in de dialoog. */
  readonly refresh: () => Promise<void>
  /** Bekijk advies in de lijst Wie heeft toegang: de dialoog dicht en het paneel op die kaart. */
  readonly showRequest: (adviesId: string) => void
  /**
   * De laatste lezing van `GET /delingen` aan de interne kant, voor de labels in de aanwezigheid en bij
   * opmerkingen (G-19); aan de externe kant leest `useSharingOverview` hetzelfde overzicht.
   */
  readonly overview: ReadOverview | undefined
}

const NO_ADVICE: ReadAdvice = { kanVragen: false, buitenNwo: false, uitgesloten: [], verzoeken: [] }
const POLL_MS = 30_000
/** Wat de hook van de projectie leest. */
type ProjectionShape = Pick<Projection, 'kant' | 'magDelen' | 'secties' | 'titel'>
/** Een lege projectie aan de externe kant, zolang de echte er nog niet is: dan vraagt de hook niets op. */
const EMPTY: ProjectionShape = { kant: 'extern', magDelen: false, secties: [], titel: { nl: '', en: '' } }

function logChain(chain: Readonly<Record<string, unknown>>): void {
  console.error('eformulieren: advies niet gelukt', chain)
}

function lineOf(request: AdviceRecord): AdviceLine {
  return {
    id: request.id,
    uitnodigingId: request.uitnodigingId,
    toestand: request.toestand,
    anker: { soort: request.anker.soort, label: request.anker.label },
    termijn: request.termijn,
    adviseurKant: request.adviseurKant,
    gedeeld: request.gedeeld,
  }
}

export function useAdvice(options: AdviceOptions): PageAdvice {
  const { taal: language, instantieId, ik: me } = options
  const projection: ProjectionShape = options.projectie ?? EMPTY
  const internal = projection.kant === 'intern' && projection.magDelen
  const internalViewer = options.projectie !== undefined && projection.kant === 'intern'
  const headerKey = JSON.stringify(options.headers)
  const callbacks = useRef(options)
  callbacks.current = options
  const client = useMemo(
    () =>
      createShareClient({
        instantieId,
        headers: JSON.parse(headerKey) as Readonly<Record<string, string>>,
        taal: language,
        sectionName: (id) => projection.secties.find((section) => section.id === id)?.titel[language],
        fetch: (input, init) => globalThis.fetch(input, init),
        onSessionExpired: () => callbacks.current.onSessionExpired(),
        log: logChain,
      }),
    [instantieId, headerKey, language, projection],
  )

  const [read, setRead] = useState<ReadAdviceFull | 'laden' | undefined>('laden')
  const [overview, setOverview] = useState<ReadOverview | undefined>(undefined)

  const refresh = useCallback(async (): Promise<void> => {
    if (!internalViewer) return
    const [advice, lines] = await Promise.all([client.adviceFull(), client.overview()])
    setRead(advice)
    if (lines !== 'mislukt') setOverview(lines)
  }, [client, internalViewer])

  useEffect(() => {
    if (!internalViewer) return
    void refresh()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [internalViewer, refresh])

  const [panelShow, setPanelShow] = useState<{ readonly verzoek?: string; readonly doel?: AdviceTarget; readonly teller: number } | undefined>(undefined)
  const [showCount, setShowCount] = useState(0)
  const showPanel = useCallback((what: { readonly verzoek?: string; readonly doel?: AdviceTarget }) => {
    setShowCount((count) => count + 1)
    setPanelShow((previous) => ({ ...what, teller: (previous?.teller ?? 0) + 1 }))
  }, [])

  const loaded = read === 'laden' ? undefined : read
  const asked = useMemo(() => panelRequests(loaded), [loaded])
  const fieldNames = useMemo(() => {
    const names = new Map<string, string>()
    for (const section of projection.secties) for (const field of section.velden) if (field.label !== undefined) names.set(field.id, field.label[language])
    return names
  }, [projection, language])
  const views = useMemo(
    () =>
      adviceViews(asked, {
        ik: me,
        taal: language,
        sectionName: (id) => projection.secties.find((section) => section.id === id)?.titel[language],
        fieldName: (id) => fieldNames.get(id),
        formName: projection.titel[language],
        emailOfInvitation: (id) => overview?.delingen.find((line) => line.id === id)?.email,
        labels: overview?.labels ?? {},
      }),
    [asked, me, language, projection, fieldNames, overview],
  )

  // Wat er sinds de vorige lezing nieuw is, voor de live regio van het formulier (buiten het paneel).
  const previousViews = useRef<typeof views | undefined>(undefined)
  const [news, setNews] = useState('')
  useEffect(() => {
    if (read === 'laden') return
    const before = previousViews.current
    previousViews.current = views
    if (before === undefined) return
    const said = adviceNews(before, views, language)
    if (said.length > 0) setNews(said.join(' '))
  }, [views, read, language])

  const forDialog: ReadAdvice | 'laden' = !internal
    ? NO_ADVICE
    : read === 'laden'
      ? 'laden'
      : read === undefined
        ? NO_ADVICE
        : { kanVragen: read.kanVragen, buitenNwo: read.buitenNwo, uitgesloten: read.uitgesloten, verzoeken: asked.map(lineOf) }

  const shown = showAdvicePanel(projection.kant, loaded)
  const canAsk = loaded?.kanVragen === true && internal
  const after = useCallback(
    async <T,>(outcome: Promise<T>): Promise<T> => {
      const done = await outcome
      await refresh()
      return done
    },
    [refresh],
  )

  const marks: AdviceMarks | undefined = !shown
    ? undefined
    : {
        kanVragen: canAsk,
        uitgesloten: loaded?.uitgesloten ?? [],
        aantal: (target) => adviceCountFor(views, target),
        onVragen: (anchor) => callbacks.current.onOpenWith({ rol: 'advies', anker: anchor }),
        onToon: (target) => showPanel({ doel: target }),
      }
  const sidePanel: SidePanel | undefined = !shown
    ? undefined
    : {
        knop: language === 'nl' ? 'Advies' : 'Advice',
        teller: { aantal: views.length, woorden: adviceRequestCount(views.length, language) },
        toon: showCount,
        melding: news,
        render: (context) => (
          <AdvicePanel
            taal={language}
            meldtZelf={false}
            verzoeken={views}
            kanVragen={canAsk}
            {...(context.actieveSectie === undefined ? {} : { actieveSectie: context.actieveSectie })}
            {...(panelShow === undefined ? {} : { toon: panelShow })}
            onVragen={() => callbacks.current.onOpenWith({ rol: 'advies', anker: { soort: 'formulier' } })}
            onAnker={context.springNaar}
            onReageren={(id, text) => after(client.react(id, text))}
            onAfronden={(id) => after(client.finish(id))}
            onHerinneren={(id) => after(client.remind(id))}
            onIntrekken={(id) => after(client.withdrawAdvice(id))}
            onTermijn={(id, deadline) => after(client.deadline(id, deadline))}
            onBelangOordeel={(id, outcome, explanation) => after(client.judge(id, outcome, explanation))}
            // Wie aan de interne kant kijkt zonder te mogen delen, is hier functioneel beheer in een
            // opening: die beoordeelt alleen de verklaring (R-155f, R-184k).
            alleenOordeel={!projection.magDelen}
          />
        ),
      }

  return {
    overview,
    forDialog,
    marks,
    sidePanel,
    refresh,
    showRequest: (adviesId) => {
      callbacks.current.onOpenWith(undefined)
      // Na het sluiten: de dialoog zet bij het sluiten de focus terug op de knop in de kop, en het
      // paneel zet hem daarna op de kaart.
      setTimeout(() => showPanel({ verzoek: adviesId }), 0)
    },
  }
}

/** Het aantal verzoeken in woorden, voor de naam van de knop Advies ("Advies: 3 verzoeken"). */
function adviceRequestCount(count: number, language: Language): string {
  if (language === 'nl') return count === 1 ? '1 verzoek' : `${String(count)} verzoeken`
  return count === 1 ? '1 request' : `${String(count)} requests`
}
